import {
  Row,
  Card,
  Typography,
  Col,
  Avatar,
  Image,
  Select,
  Button,
  Space,
  Checkbox,
  Modal,
  message,
} from "antd";
import {
  SettingOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import React, { useState, useEffect } from "react";
import { getCardStyle } from "../../../../utils/getCardStyle";
import { Tag } from "../../../../../node_modules/antd/es/index";
import { POSTSyncArticuloMultiplo } from "../../../../helpers/articulos/POSTSyncArticuloMultiplo";

const { Text } = Typography;

const usaLote = process.env.REACT_APP_USALOTE;

const PaletCard = ({
  palet,
  darkMode,
  tiposPalet,
  alturasPalet,
  ordenActiva,
  setModalVisible,
  isActive = false,
  onActualizarConfiguracion,
  onCalcularCapacidad,
  onEliminarPalet,
  onActivarPalet,
  detallesLocal,
  onMultiploAplicado,
}) => {
  const tipoPaletCurrent = tiposPalet.find((t) => t.idpalt === palet.idpalt);
  const alturaPaletCurrent = alturasPalet.find(
    (t) => t.idpala === palet.idpala,
  );

  const [isEditing, setIsEditing] = useState(false);
  const [tempTipo, setTempTipo] = useState(palet.idpalt || null);
  const [tempAltura, setTempAltura] = useState(palet.idpala || null);
  const [mantenerConfig, setMantenerConfig] = useState(false);
  const [capacidadManual, setCapacidadManual] = useState(null); // Para restar manualmente unidades del calculo de capacidad
  const [capacidadTope, setCapacidadTope] = useState(null); // Referencia para saber hasta donde +1

  const [showAvatar, setShowAvatar] = useState(true);

  useEffect(() => {
    const checkScreenSize = () => {
      // Ocultar avatar si el ancho es menor a 768px (pantalla móvil)
      setShowAvatar(window.innerWidth >= 467);
    };

    // Ejecutar al montar
    checkScreenSize();

    // Escuchar cambios de tamaño
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Calcular capacidad base (la que viene del cálculo)
  const capacidadBase = React.useMemo(() => {
    if (!tempTipo || !tempAltura || !onCalcularCapacidad || !palet?.idPalet) {
      return null;
    }
    return onCalcularCapacidad(palet?.idPalet, tempTipo, tempAltura);
  }, [tempTipo, tempAltura, onCalcularCapacidad, palet?.idPalet]);

  // Guardar el tope cuando cambia la capacidad base
  useEffect(() => {
    if (capacidadBase !== null) {
      setCapacidadTope(capacidadBase);
    }
  }, [capacidadBase]);

  // Determinar el valor final (ajustado manualmente, pero dentro de límites)
  const capacidadFinal = React.useMemo(() => {
    if (capacidadManual !== null) {
      // Asegurar que esté entre 1 y capacidadTope
      return Math.max(1, Math.min(capacidadManual, capacidadTope || Infinity));
    }
    return capacidadBase;
  }, [capacidadManual, capacidadBase, capacidadTope]);

  useEffect(() => {
    setTempTipo(palet.idpalt || null);
    setTempAltura(palet.idpala || null);
  }, [palet.idpalt, palet.idpala]);

  const handleEditarClick = () => {
    setTempTipo(palet.idpalt || null);
    setTempAltura(palet.idpala || null);
    setMantenerConfig(false);
    setIsEditing(true);
  };

  const handleAceptar = () => {
    if (capacidadFinal === null) return; // seguridad

    if (onActualizarConfiguracion) {
      onActualizarConfiguracion(
        palet.idPalet,
        tempTipo,
        tempAltura,
        mantenerConfig,
        capacidadFinal,
      );
    }
    setIsEditing(false);
  };

  const handleCancelar = () => {
    setIsEditing(false);
  };

  const extraerMaterialCodeDeCb3 = (cb3) => {
    if (!cb3) return null;
    const clean = String(cb3).trim().replace(/[()]/g, "").toUpperCase();
    let pos = 0;
    if (clean.startsWith("00")) pos = 2;
    pos += 18;
    if (clean.slice(pos, pos + 3) === "240") pos += 3;
    return clean.slice(pos);
  };

  const handleMultiploChange = async (multip) => {
    const item = (detallesLocal ?? []).find(
      (d) => String(d.sku).trim() === String(palet.sku).trim(),
    );
    const data = {
      idartl: item?.idartl,
      matcod: extraerMaterialCodeDeCb3(palet.cb3),
      multip,
    };

    if (!data.idartl || !data.matcod || !data.multip) {
      message.error(
        "Faltan datos para sincronizar el múltiplo (idartl, matcod o multip).",
      );
      return;
    }

    const response = await POSTSyncArticuloMultiplo(
      data.idartl,
      data.matcod,
      data.multip,
    );
    if (response?.data?.result === false) {
      message.error(response.data.descri);
    } else if (response?.data?.result === true) {
      onMultiploAplicado?.(data.idartl, data.multip, palet.idPalet);
    }
  };

  return (
    <Col span={24} key={palet.idPalet} id={`palet-${palet.idPalet}`}>
      <Card
        onClick={() => {
          if (onActivarPalet) {
            if (!palet?.esPaletLote) {
              onActivarPalet(palet.idPalet);
            }
          }
        }}
        hoverable
        styles={{ body: { padding: 16 } }}
        style={{
          ...getCardStyle(darkMode),
          height: "100%",
          flex: 1,
          marginBottom: 8,
          backgroundColor: "#fafafa",
          transition: "background-color 0.3s ease, border 0.3s ease",
          border: isActive ? `2px solid #1890ff` : "1px solid #e8e8e8",
          borderRadius: isActive ? 8 : 6,
          boxShadow: isActive
            ? darkMode
              ? "0 0 0 2px rgba(24, 144, 255, 0.3)"
              : "0 0 0 2px rgba(24, 144, 255, 0.2)"
            : "none",
        }}
      >
        {/* FILA 1: ID y SKU */}
        <Row align="top" justify="space-between">
          <Col style={{ minWidth: 0 }}>
            <Text
              strong
              style={{
                fontSize: 20,
                color: "#000",
                display: "block",
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              #{palet?.alias ? palet.alias : palet.idPalet}
            </Text>
          </Col>
          <Col style={{ minWidth: 0, textAlign: "right" }}>
            <Text
              strong
              style={{
                fontSize: 20,
                color: "#000",
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {palet?.partes?.length > 1 ? palet.sku.slice(0, 6) : palet.sku}
            </Text>
          </Col>
        </Row>

        {/* FILA 2: Tipo y altura de palet + Avatar */}
        <Row style={{ marginTop: 8 }}>
          <Col flex="auto">
            {palet.idpalt && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <Text style={{ fontSize: "15px", color: "#888" }}>
                  {tipoPaletCurrent?.descri}
                </Text>
                {(tipoPaletCurrent?.medpro || tipoPaletCurrent?.medanc) && (
                  <Text style={{ fontSize: "15px", color: "#777" }}>
                    {tipoPaletCurrent.medpro}×{tipoPaletCurrent.medanc}
                  </Text>
                )}
                {(tipoPaletCurrent?.medpro || tipoPaletCurrent?.medanc) &&
                  alturaPaletCurrent && (
                    <Text style={{ color: "#ccc" }}>—</Text>
                  )}
                {alturaPaletCurrent ? (
                  <Text style={{ fontSize: "15px", color: "#555" }}>
                    {`${alturaPaletCurrent.nombre} (${alturaPaletCurrent.medalt} cm)`}
                  </Text>
                ) : palet.idpala ? (
                  <Text style={{ fontSize: "15px", color: "#555" }}>
                    {palet.idpala}
                  </Text>
                ) : null}
                {palet.asignado === 0 && ordenActiva && !palet?.esPaletLote && (
                  <SettingOutlined
                    style={{
                      fontSize: 24,
                      color: "#FB9A1A",
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditarClick();
                    }}
                  />
                )}
              </div>
            )}
            <Text
              style={{
                fontSize: "17px",
                color: "#000",
                marginTop: palet.idpalt ? 6 : 0,
                display: "block",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {palet.nombre}
            </Text>

            {!palet.esMultiparte && (
              <div style={{ marginTop: 8 }}>
                {(palet.lote || palet.vto) && (
                  <div
                    style={{
                      padding: "6px 0",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Text style={{ fontSize: 15, color: "black" }}>
                      {palet.lote && (
                        <span>
                          <Text style={{ color: "black" }} strong>
                            L:
                          </Text>{" "}
                          {palet.lote}
                        </span>
                      )}
                      <span> {" • "} </span>
                      {palet.vto && (
                        <span>
                          <Text style={{ color: "black" }} strong>
                            V:
                          </Text>{" "}
                          {palet.vto}
                        </span>
                      )}
                    </Text>
                  </div>
                )}
              </div>
            )}
          </Col>

          {showAvatar && (
            <Col flex="none" style={{ marginLeft: "auto", marginTop: 0 }}>
              <Avatar
                src="/assets/palet.png"
                size={60}
                style={{ backgroundColor: "#d5c279" }}
              />
            </Col>
          )}
        </Row>

        {/* FILA 3: Partes (si es multipartes) */}
        {palet.esMultiparte && palet.partes?.length > 0 && detallesLocal && (
          <div style={{ marginTop: 8 }}>
            {palet.partes.map((parte, idx, arr) => {
              const item = detallesLocal.find((d) => {
                if (d.idartp != null) {
                  return d.idartp === parte.idartp && d.nropar === parte.nropar;
                }
                // Granel: idartp/nropar llegan null en ambos y colisionarían.
                return (
                  String(d.idartl ?? "").trim() ===
                  String(parte.idartl ?? "").trim()
                );
              });
              const cantidadAsignada =
                palet.asignadoPorParte?.[parte.idartp] || 0;
              if (!item) return null;
              return (
                <div
                  key={`${parte.idartp}-${parte.nropar}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "end",
                    padding: "6px 0",
                    borderBottom:
                      idx < arr.length - 1 ? "1px solid #e8e8e8" : "none",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
                    <Text
                      style={{
                        color: "#000",
                        fontSize: 15,
                        fontWeight: 500,
                      }}
                    >
                      {item.sku}
                    </Text>
                    <Text
                      style={{
                        color: "#000",
                        fontSize: 15,
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        marginTop: 2,
                      }}
                    >
                      {item.descri} / {item.nombre || "-"}
                    </Text>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Text
                      strong
                      style={{
                        fontSize: 20,
                        textAlign: "right",
                        color: "#000",
                      }}
                    >
                      {cantidadAsignada} u.
                    </Text>
                    {cantidadAsignada > 0 && isActive && (
                      <Image
                        src="/icons/keyboard.png"
                        preview={false}
                        style={{
                          width: 32,
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setModalVisible({
                            status: true,
                            payload: {
                              sku: item.sku,
                              nombre: item.nombre,
                              parte: parte.nropar,
                              idartp: parte.idartp,
                              nropar: parte.nropar,
                              idPalet: palet.idPalet,
                              valorInicial: cantidadAsignada,
                              origen: "palet",
                            },
                          });
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modo edición (si aplica) */}
        {isEditing && (
          <div style={{ marginTop: 8 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 8,
                alignItems: "flex-start",
              }}
            >
              <Select
                size="medium"
                value={tempTipo || undefined}
                onChange={setTempTipo}
                style={{ width: 200, fontSize: "14px" }}
                options={tiposPalet.map((t) => ({
                  label: `${t.descri} (${t.medpro}×${t.medanc})`,
                  value: t.idpalt,
                }))}
                placeholder="Tipo de palet"
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              />
              <Select
                size="medium"
                value={tempAltura || undefined}
                onChange={setTempAltura}
                style={{ width: 200, fontSize: "14px" }}
                options={alturasPalet.map((a) => ({
                  label: `${a.nombre} (${a.medalt} cm)`,
                  value: a.idpala,
                }))}
                placeholder="Altura"
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              />

              {/* PREVIEW DE CAPACIDAD + BOTONES –1 / +1 */}
              {capacidadFinal !== null && (
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  {/* Botón –1 (izquierda) */}
                  {capacidadFinal > 1 && (
                    <Button
                      type="text"
                      size="small"
                      style={{
                        height: 32,
                        width: 32,
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      icon={
                        <MinusCircleOutlined
                          style={{ fontSize: 24, color: "#FB9A1A" }}
                        />
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        setCapacidadManual((prev) =>
                          Math.max(1, (prev ?? capacidadFinal) - 1),
                        );
                      }}
                    />
                  )}

                  {/* Tag central */}
                  <Tag
                    style={{
                      fontSize: 14,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      minWidth: 80,
                      justifyContent: "center",
                    }}
                  >
                    {capacidadFinal} Unid.
                  </Tag>

                  {/* Botón +1 (derecha) */}
                  {capacidadFinal < (capacidadTope || capacidadFinal) && (
                    <Button
                      type="text"
                      size="small"
                      style={{
                        height: 32,
                        width: 32,
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      icon={
                        <PlusCircleOutlined
                          style={{ fontSize: 24, color: "#FB9A1A" }}
                        />
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        setCapacidadManual((prev) =>
                          Math.min(
                            capacidadTope || Infinity,
                            (prev ?? capacidadFinal) + 1,
                          ),
                        );
                      }}
                    />
                  )}
                </div>
              )}
            </div>
            <Space direction="vertical" size={8} style={{ marginTop: 8 }}>
              <Checkbox
                checked={mantenerConfig}
                style={{ color: "grey" }}
                onChange={(e) => setMantenerConfig(e.target.checked)}
              >
                Mantener como configuración predeterminada
              </Checkbox>
              <Space>
                <Button size="medium" type="primary" onClick={handleAceptar}>
                  Aceptar
                </Button>
                <Button size="medium" onClick={handleCancelar}>
                  Cancelar
                </Button>
              </Space>
            </Space>
          </div>
        )}

        {/* FILA 3: Cantidades */}
        <Row justify="space-between" align="middle">
          <Col>
            {!palet.esMultiparte &&
              ordenActiva &&
              isActive &&
              palet.asignado >= 1 && (
                <Image
                  className="custom-icon"
                  src="/icons/keyboard.png"
                  preview={false}
                  style={{ width: 36, cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalVisible({
                      status: true,
                      payload: {
                        ...palet,
                        origen: "palet",
                        sku: palet.sku,
                        nombre: palet.nombre,
                        parte: palet.nropar || "-",
                        modelo: palet.modelo || "-",
                        ubicacion: "-",
                        cantidad: palet.ctdPorPalet,
                        valorInicial: palet.asignado,
                        idartp: palet.idartp,
                        nropar: palet.nropar,
                        idPalet: palet.idPalet,
                      },
                    });
                  }}
                />
              )}
          </Col>

          <Col style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {!isEditing && (
              <Text
                strong
                style={{
                  fontSize: 20,
                  textAlign: "right",
                  color: "#000",
                }}
              >
                {usaLote === "true"
                  ? `${palet.asignado} Unid.`
                  : `${palet.asignado} de ${palet.ctdPorPalet}`}
              </Text>
            )}

            {usaLote === "true" && ordenActiva && palet.cb1 && palet.cb2 && palet.cb3 && (
              <Select
                value={palet.multiploAplicado || 1}
                style={{ width: 60 }}
                options={Array.from({ length: 10 }, (_, i) => ({
                  label: `x${i + 1}`,
                  value: i + 1,
                }))}
                onChange={handleMultiploChange}
              />
            )}
          </Col>
        </Row>

        <Row justify="space-between" align="middle" style={{ marginTop: 12 }}>
          <Col />

          {ordenActiva && (
            <Button
              danger
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                Modal.confirm({
                  title: "¿Quitar palet?",
                  content: ``,
                  okText: "Sí, quitar",
                  okType: "danger",
                  cancelText: "Cancelar",
                  onOk: () => {
                    onEliminarPalet?.(palet.idPalet);
                  },
                });
              }}
            >
              Deshacer palet
            </Button>
          )}
        </Row>
      </Card>
    </Col>
  );
};

export default PaletCard;
